import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Users,
  Building2,
  Phone,
  Loader2,
  Calendar,
  Pencil,
  LayoutDashboard,
} from 'lucide-react';
import { PhotoTagEntityType, EntityType } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useVenue, useVenueEvents } from '../hooks/useVenues';
import { useRooms } from '../hooks/useFloorPlans';
import { ContactListView } from '../components/contacts';
import { PhotoListView } from '../components/photos/PhotoListView';
import { EntityModal, PageSectionsLayout, StatTile } from '../components/common';
import { eventsListPath } from '../utils/eventListLink';
import type { SectionDef } from '../components/common';
import { VenueDetailPanel, VenueRoomListView, VenueFloorPlanListView } from '../components/venues';
import { RecordNotes } from '../components/assistant/RecordNotes';
import { ContactMoreMenu } from '../components/common/ContactMoreMenu';
import { PhoneLink } from '../components/common/PhoneLink';

export function VenueDetailPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { data: venue, isLoading } = useVenue(venueId || '');
  const [showEdit, setShowEdit] = useState(false);
  const { data: events = [] } = useVenueEvents(venueId || '');
  const { data: rooms = [] } = useRooms(venueId || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Venue not found</p>
        <button
          onClick={() => navigate('/venues')}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm"
        >
          Back to Venues
        </button>
      </div>
    );
  }

  const sections: SectionDef[] = [
    {
      id: 'contacts',
      title: 'Contacts',
      content: <ContactListView entityType={EntityType.VENUE} entityId={venueId!} />,
    },
    {
      id: 'rooms',
      title: 'Rooms',
      content: <VenueRoomListView venueId={venueId!} />,
    },
    {
      id: 'floor-plans',
      title: 'Floor Plans',
      content: <VenueFloorPlanListView venueId={venueId!} />,
    },
    ...(events.length > 0
      ? [
          {
            id: 'events',
            title: `Events (${events.length})`,
            content: (
              <div className="space-y-2">
                {events.map((e: any) => (
                  <div
                    key={e.id}
                    onClick={() => navigate(`/events/${e.id}`)}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-100"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{e.name}</p>
                      <p className="text-xs text-gray-500">
                        {e.startAt
                          ? new Date(e.startAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'No date'}
                        {e.eventType?.name && ` · ${e.eventType.name}`}
                      </p>
                    </div>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs capitalize">
                      {e.status?.toLowerCase().replace('_', ' ') || '—'}
                    </span>
                  </div>
                ))}
              </div>
            ),
          },
        ]
      : []),
    {
      // The assistant's notes about the venue — a walk-through, a call with its manager.
      id: 'assistant-notes',
      title: 'Assistant notes',
      content: (
        <RecordNotes
          scope={{ type: 'venue', id: venueId!, label: venue.name, url: `/venues/${venueId}` }}
        />
      ),
    },
    {
      id: 'photos',
      title: 'Photos',
      content: (
        <PhotoListView
          entityType={PhotoTagEntityType.VENUE}
          entityId={venueId}
          limit={12}
          enableUpload
          currentUser={currentUser || undefined}
        />
      ),
    },
    ...(venue.notes
      ? [
          {
            id: 'notes',
            title: 'Notes',
            content: <p className="text-sm text-gray-600 whitespace-pre-wrap">{venue.notes}</p>,
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 size={24} className="text-primary-600" />
              {venue.name}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
              {venue.city && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {[venue.address, venue.city, venue.state].filter(Boolean).join(', ')}
                </span>
              )}
              {venue.phone && (
                <PhoneLink phone={venue.phone} name={venue.name} icon={<Phone size={14} />} />
              )}
              {venue.capacity && (
                <span className="flex items-center gap-1">
                  <Users size={14} />
                  Capacity: {venue.capacity}
                </span>
              )}
              {(venue as any).vendorName && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  Operator: {(venue as any).vendorName}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={16} />
              Edit
            </button>
            <ContactMoreMenu email={venue.email} />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatTile
          label="Events"
          value={events.length}
          icon={Calendar}
          color="bg-blue-500"
          // Every event held here, past and cancelled included — as counted.
          to={
            events.length > 0
              ? eventsListPath({ venueIds: [venueId!], range: 'all', statuses: 'all' })
              : undefined
          }
        />
        <StatTile label="Rooms" value={rooms.length} icon={LayoutDashboard} color="bg-indigo-500" />
        <StatTile label="Contacts" value="—" icon={Users} color="bg-green-500" />
        {venue.capacity && (
          <StatTile
            label="Capacity"
            value={venue.capacity}
            icon={Building2}
            color="bg-purple-500"
          />
        )}
      </div>

      {/* Sections */}
      <PageSectionsLayout
        pageKey="venue-detail"
        userId={currentUser?.id ?? 'guest'}
        sections={sections}
      />

      {/* Edit Modal */}
      {currentUser && (
        <EntityModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          title={`Edit: ${venue.name}`}
          size="lg"
        >
          <VenueDetailPanel
            venueId={venueId!}
            onClose={() => setShowEdit(false)}
            onUnsavedChange={() => {}}
            currentUser={currentUser}
          />
        </EntityModal>
      )}
    </div>
  );
}
